<template>
  <div
    class="modal fade"
    id="AffectationCategorieModal"
    tabindex="-1"
    role="dialog"
    aria-labelledby="AffectationCategorieModalLabel"
    aria-hidden="true"
    ref="modalRef"
  >
    <div class="modal-dialog modal-dialog-centered modal-md" role="document">
      <div class="modal-content">
        <div class="modal-header">
          <h4 class="modal-title">
            Affecter une catégorie client à {{ selectedClient?.nomClient || "un client" }} {{ selectedClient?.prenomClient || "" }}
          </h4>
          <button
            type="button"
            class="btn-close py-0"
            data-bs-dismiss="modal"
            aria-label="Close"
            @click="resetForm"
          ></button>
        </div>
        <div class="modal-body">
          <Form ref="affectationForm" :validation-schema="affectationSchema" @submit="handleSubmit">
            <div class="row">
              <div class="col-12">
                <div class="form-group mb-15 mb-sm-20 mb-md-25">
                  <label class="d-block text-black fw-semibold mb-10">
                    Client <span class="text-danger">*</span>
                  </label>
                  <Field name="clientId" v-slot="{ field, errors }">
                    <Multiselect
                      v-model="selectedClientId"
                      :options="clientOptions"
                      :preserve-search="true"
                      :multiple="false"
                      :searchable="true"
                      placeholder="Sélectionner le client"
                      label="label"
                      track-by="value"
                      :class="{ 'is-invalid': errors.length }"
                      :disabled="true"
                      @update:modelValue="field.onChange($event)"
                    />
                    <ErrorMessage name="clientId" class="text-danger" />
                  </Field>
                </div>
                <div class="form-group mb-15 mb-sm-20 mb-md-25">
                  <label class="d-block text-black fw-semibold mb-10">
                    Catégorie Client <span class="text-danger">*</span>
                  </label>
                  <Field name="categorieClientId" v-slot="{ field, errors }">
                    <Multiselect
                      v-model="selectedCategorieClientId"
                      :options="categorieClientsOptions"
                      :preserve-search="true"
                      :multiple="false"
                      :searchable="true"
                      placeholder="Sélectionner une catégorie client"
                      label="label"
                      track-by="value"
                      :class="{ 'is-invalid': errors.length }"
                      @update:modelValue="field.onChange($event)"
                    />
                    <ErrorMessage name="categorieClientId" class="text-danger" />
                  </Field>
                </div>
                <div class="d-flex justify-content-end">
                  <button
                    type="button"
                    class="btn btn-secondary me-2"
                    data-bs-dismiss="modal"
                    @click="resetForm"
                  >
                    Annuler
                  </button>
                  <button
                    type="submit"
                    class="btn btn-primary"
                  >
                    Affecter
                  </button>
                </div>
              </div>
            </div>
          </Form>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent, ref, computed, onMounted, watch, nextTick } from "vue";
import { Form, Field, ErrorMessage } from "vee-validate";
import * as Yup from "yup";
import ApiService from "@/services/ApiService";
import { error, success, hideModal } from "@/utils/utils";
import { Client } from "@/models/Client";
import { CategorieClient } from "@/models/CategorieClient";
import Multiselect from "@vueform/multiselect";

interface FormValues {
  clientId: number | null;
  categorieClientId: number | null;
}

export default defineComponent({
  name: "AffectationCategorieModal",
  components: {
    Form,
    Field,
    ErrorMessage,
    Multiselect,
  },
  props: {
    selectedClient: {
      type: Object as () => Client | null,
      default: null,
    },
  },
  emits: ["affectee", "openmodal"],
  setup(props, { emit }) {
    const modalRef = ref<HTMLElement | null>(null);
    const affectationForm = ref<InstanceType<typeof Form> | null>(null);
    const clients = ref<Client[]>([]);
    const categorieClients = ref<CategorieClient[]>([]);
  // Changer les types des refs
  const selectedClientId = ref<number | null>(null);
  const selectedCategorieClientId = ref<number | null>(null);
      
      
    const affectationSchema = Yup.object().shape({
      clientId: Yup.number()
        .required("Le client est obligatoire")
        .typeError("Veuillez sélectionner un client"),
      categorieClientId: Yup.number()
        .required("La categorie client est obligatoire")
        .typeError("Veuillez sélectionner une categorie client"),
    });

    // Options pour Multiselect
    const clientOptions = computed(() => {
      return clients.value.map((client) => ({
        value: client.id,
        label: `${client.nomClient} ${client.prenomClient}`,
      }));
    });

    const categorieClientsOptions = computed(() => {
      return categorieClients.value.map((categorieClient) => ({
        value: categorieClient.id,
        label: `${categorieClient.libelle}`,
        disabled: props.selectedClient?.categorieClient?.id === categorieClient.id,
      }));
    });

    const getAllClient = async () => {
      try {
        const { data } = await ApiService.get("/all/clients");
        console.log("dataaaaaaaa",data);
        clients.value = data.data || [];
      } catch (err: any) {
        error(err.response?.data?.message || "Erreur lors de la récupération des utilisateurs");
      }
    };

    const fetchAllCategorieClient = async () => {
      try {
        const { data } = await ApiService.get("/all/categorieclient?page=1&limit=1000");
        categorieClients.value = data.data.data || [];
      } catch (err: any) {
        error(err.response?.data?.message || "Erreur lors de la récupération des categories clients");
      }
    };

    const handleSubmit = async (values: FormValues) => {
      try {
        console.log("Valeurs soumises :", values);
        await ApiService.patch(`/clients/${values.clientId}/category`, {
          categorieClientId: values.categorieClientId,
        });
        success("Catégorie client affectée avec succès");
        emit("affectee");
        resetForm();
        hideModal(modalRef.value);
      } catch (err: any) {
        error(err.response?.data?.message || "Erreur lors de l'affectation de la catégorie");
      }
    };

    const resetForm = () => {
      selectedClientId.value = null;
      selectedCategorieClientId.value = null;
      affectationForm.value?.resetForm();
    };
watch(
  () => props.selectedClient,
  async (newClient) => {
    if (newClient) {
      await getAllClient();

      selectedClientId.value = newClient.id;
     
      const categorieActive = newClient.clientCategorieClients?.find(
        (cc: any) => cc.statut === "actif"
      )?.categorieClient || newClient.clientCategorieClients?.[0]?.categorieClient || null;

      selectedCategorieClientId.value = categorieActive?.id || null;

      nextTick(() => {
        affectationForm.value?.setFieldValue("clientId", newClient.id);
        affectationForm.value?.setFieldValue("categorieClientId", categorieActive?.id || null);
      });
    }
  },
  { immediate: true }
);

watch(clientOptions, (options) => {
 
  if (options.length > 0 && props.selectedClient) {
    selectedClientId.value = props.selectedClient.id;
    nextTick(() => {
      affectationForm.value?.setFieldValue("clientId", props.selectedClient.id);
    });
  }
});
watch(selectedClientId, (newValue) => {
  affectationForm.value?.setFieldValue("clientId", newValue ?? null);
});

watch(selectedCategorieClientId, (newValue) => {
  affectationForm.value?.setFieldValue("categorieClientId", newValue ?? null);
});

    onMounted(async () => {
      await Promise.all([getAllClient(), fetchAllCategorieClient()]);
    });

    return {
      modalRef,
      affectationForm,
      clients,
      categorieClients,
      clientOptions,
      categorieClientsOptions,
      selectedClientId,
      selectedCategorieClientId,
      affectationSchema,
      handleSubmit,
      resetForm,
    };
  },
});
</script>

<style scoped>
.mb-4 {
  margin-bottom: 1.5rem !important; 
}
</style>